import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

interface CartContextType {
  count: number;
  refreshCart: () => Promise<void>;
  /** Optimistic bump after adding an item (before the refetch settles). */
  bump: (delta?: number) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

/**
 * Global cart item count shown as a badge on the Navbar / BottomNav cart
 * icons. Refetches on every auth change and after any cart mutation.
 */
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [count, setCount] = useState(0);

  const refreshCart = useCallback(async () => {
    if (!user) {
      setCount(0);
      return;
    }
    try {
      const response = await api.get('/cart');
      setCount(response.data?.items?.length ?? 0);
    } catch (error) {
      console.error('Failed to fetch cart count:', error);
    }
  }, [user]);

  const bump = useCallback((delta = 1) => {
    setCount((c) => c + delta);
  }, []);

  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  return (
    <CartContext.Provider value={{ count, refreshCart, bump }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};